import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Logout from "./Logout.tsx";

export type AppNavMode = "quiz" | "learn" | "practice" | "practice-exam" | "dashboard" | "profile";

const NAV_ITEMS: Array<{ key: AppNavMode; label: string }> = [
  { key: "quiz", label: "Quiz" },
  { key: "learn", label: "Learn" },
  { key: "practice", label: "Practice" },
  { key: "practice-exam", label: "Practice Exam" },
];

type TopNavProps = {
  activeMode: AppNavMode;
  onSelect: (mode: AppNavMode) => void;
};

const TopNav: React.FC<TopNavProps> = ({ activeMode, onSelect }) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const navButtonClass = (isActive: boolean) =>
    `text-xl font-semibold font-body whitespace-nowrap pb-1 border-b-2 transition-colors ${
      isActive
        ? "text-primary border-accent"
        : "text-gray-500 border-transparent hover:text-ink"
    }`;

  return (
    <header className="bg-white border-b border-border shadow-sm sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between gap-4">
        <div className="font-display font-bold text-3xl text-ink whitespace-nowrap">Exam Revision</div>

        <nav className="hidden md:flex items-center gap-4 md:gap-8" aria-label="Question type navigation">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => onSelect(item.key)}
              className={navButtonClass(item.key === activeMode)}
              aria-current={item.key === activeMode ? "page" : undefined}
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="text-lg font-semibold font-body text-gray-600 hover:text-ink transition-colors duration-200"
          >
            Dashboard
          </button>
          <button
            type="button"
            onClick={() => navigate("/profile")}
            className="text-lg font-semibold font-body text-gray-600 hover:text-ink transition-colors duration-200"
          >
            Profile
          </button>
          <Logout />
        </div>

        <button
          type="button"
          onClick={() => setMenuOpen((open) => !open)}
          className="md:hidden flex items-center justify-center w-10 h-10 text-ink"
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {menuOpen ? (
              <path d="M6 6l12 12M18 6l-12 12" strokeLinecap="round" />
            ) : (
              <path d="M4 6h16M4 12h16M4 18h16" strokeLinecap="round" />
            )}
          </svg>
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-border px-4 py-3 flex flex-col gap-3" aria-label="Question type navigation">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => {
                onSelect(item.key);
                setMenuOpen(false);
              }}
              className={`text-left ${navButtonClass(item.key === activeMode)}`}
              aria-current={item.key === activeMode ? "page" : undefined}
            >
              {item.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => {
              navigate("/dashboard");
              setMenuOpen(false);
            }}
            className="text-left text-lg font-semibold font-body text-gray-600 hover:text-ink transition-colors duration-200"
          >
            Dashboard
          </button>
          <button
            type="button"
            onClick={() => {
              navigate("/profile");
              setMenuOpen(false);
            }}
            className="text-left text-lg font-semibold font-body text-gray-600 hover:text-ink transition-colors duration-200"
          >
            Profile
          </button>
          <div className="text-left">
            <Logout />
          </div>
        </div>
      )}
    </header>
  );
};

export default TopNav;
